// Legal.jsx - The legal documents, and the index that lists them.
//
// The documents themselves are markdown in src/content/legal, loaded by
// lib/legal.js. Nothing is written in this file, on purpose: a legal document
// that lives inside a JSX string is a document nobody can diff, and the diff is
// the thing that proves what it said on the day somebody agreed to it.
//
// These are drafted to describe how this business actually works, and the
// commitments in them are real and enforceable. Have a lawyer review them before
// launch, and again whenever the version is bumped.
//
// LEGEND
//   1. LegalIndex   the list of every document
//   2. Contents     the in-page contents list for one document
//   3. Legal        one document, rendered

import { useState } from "react";
import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { LEGAL_DOCS, getLegalDoc } from "../lib/legal.js";
import * as analytics from "../lib/analytics.js";
import { docDate } from "../lib/format.js";
import { Reveal } from "../ui/motion.jsx";
import { Button } from "../ui/kit.jsx";
import { PageHead } from "./parts.jsx";
import { ArrowRight } from "../ui/icons.jsx";

////////// 1. INDEX //////////

export function LegalIndex() {
  return (
    <>
      <Seo
        title="Legal"
        path="/legal"
        description="Our terms and conditions, privacy policy, shipping and insurance policy, purchase agreement, anti money laundering policy, text message terms and accessibility statement."
        image={SITE.ogLegal}
      />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Legal", to: "/legal" },
      ])} />

      <PageHead
        eyebrow="Legal"
        title="Everything we ask you to agree to, in one place."
        lede="Every document below is published in full, dated, and versioned. The version you accepted is recorded against your order, and you can ask us for a copy of that record at any time."
      />

      <div className="shell pb-16 md:pb-24">
        <ul className="grid gap-px bg-rule border border-rule sm:grid-cols-2">
          {LEGAL_DOCS.filter((doc) => !doc.unlisted).map((doc) => (
            <li key={doc.key} className="bg-paper">
              <Link
                to={`/legal/${doc.key}`}
                className="group flex h-full flex-col p-6 sm:p-7 lift"
              >
                <p className="eyebrow mb-2.5">
                  Version {doc.version} &middot; {docDate(doc.effective)}
                </p>
                <h2 className="display display-sm text-ink underline-grow self-start">{doc.title}</h2>
                <p className="hint mt-2.5 leading-relaxed flex-1 measure">{doc.summary}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-ink">
                  Read it <ArrowRight size={15} className="text-bronze" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <p className="hint mt-8 measure-wide leading-relaxed">
          Something unclear? Ask us before you send anything.{" "}
          <Link to="/contact" className="link text-ink">Contact us</Link>.
        </p>
      </div>
    </>
  );
}

////////// 2. CONTENTS //////////
// Sticky beside the document on a wide screen, collapsed above it on a phone. A
// twenty eight section contract without a contents list is a document that gets
// scrolled past rather than read.

function Contents({ headings }) {
  if (headings.length < 3) return null;

  return (
    <>
      <details className="lg:hidden panel-quiet px-4 py-3 mb-8">
        <summary className="text-sm font-semibold text-ink cursor-pointer">
          Contents ({headings.length} sections)
        </summary>
        <ol className="mt-3 space-y-1.5 border-t border-rule pt-3">
          {headings.map((heading) => (
            <li key={heading.id}>
              <a href={`#${heading.id}`} className="text-[13.5px] text-muted hover:text-ink transition-colors">
                {heading.text}
              </a>
            </li>
          ))}
        </ol>
      </details>

      <nav className="hidden lg:block sticky top-24 self-start" aria-label="Contents">
        <p className="eyebrow mb-3">Contents</p>
        <ol className="space-y-1.5 border-l border-rule pl-4 max-h-[70vh] overflow-y-auto no-scrollbar">
          {headings.map((heading) => (
            <li key={heading.id}>
              <a href={`#${heading.id}`} className="block text-[13px] leading-snug text-muted hover:text-ink transition-colors">
                {heading.text}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

////////// 3. ONE DOCUMENT //////////

////////// THE ADVERTISING SWITCH //////////
// Section 7 of the privacy policy promises three ways to switch measurement off
// and this is one of them, so it has to actually work rather than record a
// preference nobody reads. It writes the flag lib/analytics.js checks before it
// injects a single script, then reloads, so the tags are gone rather than
// present and ignored.
//
// When the browser is already sending Global Privacy Control there is nothing to
// decide, and saying so is more useful than showing a switch that cannot move.

function AdvertisingChoice() {
  const [off, setOff] = useState(() => analytics.optedOut());
  const forced = analytics.gpcActive();

  return (
    <section id="advertising-switch" className="mt-10 p-5 sm:p-6 border border-rule bg-surface/50 scroll-mt-24">
      <p className="eyebrow mb-2">Advertising measurement</p>

      {forced ? (
        <>
          <h2 className="display display-sm text-ink">Your browser has already switched it off.</h2>
          <p className="hint mt-2.5 leading-relaxed measure">
            You are sending a Global Privacy Control signal, so nothing has loaded and nothing about
            you is reported. There is nothing for you to do here.
          </p>
        </>
      ) : (
        <>
          <h2 className="display display-sm text-ink">
            {off ? "Switched off in this browser." : "Switch it off in this browser."}
          </h2>
          <p className="hint mt-2.5 leading-relaxed measure">
            {off
              ? "No advertising tag will load here and nothing further about you is reported. This applies to this browser, so it has to be set again on your phone."
              : "Meta and Google measurement stops immediately in this browser. Nothing changes about your order, your offer, or how quickly you are paid."}
          </p>
          <Button
            className="mt-4"
            variant={off ? "quiet" : "primary"}
            onClick={() => { setOff(!off); if (off) analytics.optIn(); else analytics.optOut(); }}
          >
            {off ? "Turn measurement back on" : "Do not share my information"}
          </Button>
          {!off && (
            <p className="hint mt-3 leading-relaxed measure">
              Switching it off stops anything further. It cannot recall what has already been sent.
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default function Legal({ doc }) {
  const document = getLegalDoc(doc);

  return (
    <>
      <Seo
        title={document.title}
        path={`/legal/${doc}`}
        description={document.summary}
        image={SITE.ogLegal}
        // An unlisted document is published so it can be sent to somebody, not
        // so it can be found. Out of the sitemap, out of every list, and asking
        // search engines not to index it.
        noIndex={document.unlisted}
      />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Legal", to: "/legal" },
        { label: document.title, to: `/legal/${doc}` },
      ])} />

      <PageHead
        eyebrow={`Version ${document.version} · in force from ${docDate(document.effective)}`}
        title={document.title}
        lede={document.summary}
      />

      <div className="shell pb-16 md:pb-24">
        <div className="rule-fade mb-8 md:mb-10" />

        <div className="grid gap-10 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-14">
          <Contents headings={document.headings} />

          <div className="min-w-0">
            <Reveal className="prose-igs" dangerouslySetInnerHTML={{ __html: document.html }} />

            {doc === "privacy" && <AdvertisingChoice />}

            <p className="hint mt-12 pt-6 border-t border-rule leading-relaxed measure-wide">
              This document was last updated on {docDate(document.updated)}. When we change it we
              raise the version number, and the version you accepted stays recorded against your
              order. Ask us and we will send you a copy of exactly what you agreed to and when.
            </p>

            <nav className="mt-8 pt-8 border-t border-rule" aria-label="Other legal documents">
              <p className="eyebrow mb-3.5">The rest of them</p>
              <ul className="flex flex-wrap gap-x-6 gap-y-2.5">
                {LEGAL_DOCS.filter((entry) => entry.key !== doc).map((entry) => (
                  <li key={entry.key}>
                    <Link to={`/legal/${entry.key}`} className="text-sm font-semibold text-ink link">
                      {entry.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}
